// ============================================================================
// NOTIFICATIONS SERVICE
// ============================================================================

import { api } from './api';

export type NotificationType =
  | 'stale_project'
  | 'stale_session'
  | 'weekly_cost_summary'
  | 'platform_update'
  | 'data_retention_request'
  | 'project_shared'
  | 'session_completed'
  | 'mcp_token_rotated'
  | 'guest_access_request'
  | 'admin_count_drift'
  | 'content_deletion'
  | 'project_access_changed'
  | 'session_hub_pack_ready'
  | 'session_hub_objective_change';

export interface Notification {
  notificationId: string;
  type: NotificationType;
  title: string;
  message: string;
  userId?: string;
  organizationId?: string;
  linkUrl?: string;
  retentionAction?: {
    targetType: 'project' | 'session';
    targetId: string;
    targetName: string;
  };
  guestUserId?: string;
  actionTaken?: 'ignored' | 'purged' | 'approved' | 'declined';
  actionTakenAt?: string;
  actionTakenBy?: string;
  read: boolean;
  readAt?: string;
  dismissedAt?: string;
  createdAt: string;
}

export async function getNotifications(): Promise<Notification[]> {
  const response = await api.get('/api/notifications');
  return response.data.notifications;
}

export async function markRead(notificationId: string): Promise<void> {
  await api.patch(`/api/notifications/${notificationId}/read`);
}

export async function markAllRead(): Promise<void> {
  await api.post('/api/notifications/read-all');
}

export async function dismiss(notificationId: string): Promise<void> {
  await api.delete(`/api/notifications/${notificationId}`);
}

export async function restore(notificationId: string): Promise<void> {
  await api.post(`/api/notifications/${notificationId}/restore`);
}

/** Clears read notifications, except retention notices still awaiting Ignore or Purge */
export async function dismissAllRead(): Promise<string[]> {
  const resp = await api.post('/api/notifications/dismiss-read');
  return resp.data?.notificationIds ?? [];
}

/** queued: a project purge runs on the next hourly deletion pass rather than in the request */
export async function takeAction(notificationId: string, action: 'ignored' | 'purged'): Promise<{ queued: boolean }> {
  const resp = await api.post(`/api/notifications/${notificationId}/action`, { action });
  return { queued: !!resp.data?.queued };
}

export async function broadcastPlatformUpdate(title: string, message: string, linkUrl?: string): Promise<void> {
  await api.post('/api/notifications/broadcast', { title, message, linkUrl });
}
